import { BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useEffect, useMemo, useState } from 'react';

import { SubtleRouteLink } from '@/components/RouteLinks/RouteLinks';
import SideScroller from '@/components/SideScroller/SideScroller';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchSavedSearchesIfNeeded } from '@/redux/modules/savedSearch';
import { createSaveSearchKey } from '@/redux/modules/search/save/saveSearch.helpers';
import { getHasSavedSearches } from '@/redux/modules/search/save/saveSearch.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SeeAllLink } from '../SeeAllLink/SeeAllLink';
import items from './options';
import { SearchFollowSuggestionsCard } from './SearchFollowSuggestionsCard';

type Props = {
    className?: string;
    shouldUseHomePageRedesign?: boolean;
    useAltHeader?: boolean;
};

export const SearchFollowSuggestions = ({ className, shouldUseHomePageRedesign = false, useAltHeader }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const hasSavedSearches = useAppSelector(getHasSavedSearches);
    const [startedWithSavedSearches] = useState(hasSavedSearches);

    useEffect(() => {
        dispatch(fetchSavedSearchesIfNeeded());
    }, [dispatch, isLoggedIn]);

    const carouselCards = useMemo(
        () =>
            items?.map((item, index) => ({
                card: (
                    <SearchFollowSuggestionsCard
                        browseDimension={item.browseDimension}
                        categoryId={item.categoryId}
                        categorySlug={item.categorySlug}
                        followerCount={item.followerCount}
                        images={item.images}
                        itemCount={item.itemCount}
                        name={item.name}
                        saveSearchStateKey={createSaveSearchKey(item.categoryId)}
                        shouldUseHomePageRedesign={shouldUseHomePageRedesign}
                    />
                ),
                key: `${index}-${item.categoryId}`,
            })),
        [shouldUseHomePageRedesign]
    );

    if (startedWithSavedSearches) {
        return null;
    }

    return (
        <div
            className={cn('flex flex-col', className)}
            id="followedSearch"
        >
            {useAltHeader ? (
                <div className="mx-[20px] mt-0 mb-[20px] w-full max-w-[1200px] self-center font-secondary text-[18px] text-text-primary">
                    {formatMessage(['searchFollowSuggestions', 'altHeader'])}
                </div>
            ) : (
                <>
                    {shouldUseHomePageRedesign ? (
                        <div className="mb-sm flex w-full items-center justify-between gap-sm">
                            <div className="flex min-w-0 flex-col gap-2xs">
                                <H2>{formatMessage(['searchFollowSuggestions', 'header'])}</H2>
                                <BodySecondary color="secondary">
                                    {formatMessage(['searchFollowSuggestions', 'subTitle'])}
                                </BodySecondary>
                            </div>
                            <SubtleRouteLink
                                className="shrink-0"
                                to="/dashboard/followed-searches"
                            >
                                {formatMessage(['searchFollowSuggestions', 'manage'])}
                                {' +'}
                            </SubtleRouteLink>
                        </div>
                    ) : (
                        <>
                            <div className="mb-2xs flex">
                                <div className="flex flex-auto flex-row items-baseline justify-start text-text-primary xsMax:ml-[20px] [&_h2]:m-0 [&_h2]:mr-[15px] [&_h2]:inline-block [&_h2]:p-0">
                                    <H2>{formatMessage(['searchFollowSuggestions', 'header'])}</H2>
                                    <SeeAllLink
                                        text={String(formatMessage(['searchFollowSuggestions', 'manage']))}
                                        url="/dashboard/followed-searches"
                                    />
                                </div>
                            </div>
                            <BodySecondary
                                className="mb-[20px] tracking-[0.3px] xsMax:ml-[20px]"
                                color="secondary"
                            >
                                {formatMessage(['searchFollowSuggestions', 'subTitle'])}
                            </BodySecondary>
                        </>
                    )}
                </>
            )}
            <SideScroller
                cards={carouselCards}
                className={
                    shouldUseHomePageRedesign
                        ? "w-full max-w-full [&_[class*='mt-sm']]:mt-0 [&_[role='list']]:pb-[1px]"
                        : 'max-w-[1200px]'
                }
                useOverlayControls
            />
        </div>
    );
};
